export default class UcdExplorer extends HTMLElement {
  connectedCallback () {
    if (this.initialized) return

    this.initialized = true
    this.activeRow = null
    this.activeMenu = null

    this.addEventListener('click', this.onClick)
    this.addEventListener('dblclick', this.onDoubleClick)
    this.addEventListener('contextmenu', this.onContextMenu)

    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('resize', this.closeContextMenu)
    window.addEventListener('scroll', this.closeContextMenu, true)
  }

  disconnectedCallback () {
    this.removeEventListener('click', this.onClick)
    this.removeEventListener('dblclick', this.onDoubleClick)
    this.removeEventListener('contextmenu', this.onContextMenu)

    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('resize', this.closeContextMenu)
    window.removeEventListener('scroll', this.closeContextMenu, true)

    this.closeContextMenu()
    this.initialized = false
  }

  getRows = () => {
    return Array.from(
      this.querySelectorAll('[data-ucd-explorer-row]')
    )
  }

  findRow = (target) => {
    return target.closest('[data-ucd-explorer-row]')
  }

  getMenu = (row) => {
    const menuId = row?.dataset.ucdContextMenuId

    if (!menuId) return null

    return document.getElementById(menuId)
  }

  selectRow = (row, additive = false) => {
    if (!row) return

    if (!additive) {
      this.getRows().forEach((item) => {
        if (item !== row) {
          item.classList.remove('ucd-explorer-selected')
          item.setAttribute('aria-selected', 'false')
        }
      })
    }

    row.classList.add('ucd-explorer-selected')
    row.setAttribute('aria-selected', 'true')
    this.activeRow = row
  }

  clearSelection = () => {
    this.getRows().forEach((row) => {
      row.classList.remove('ucd-explorer-selected')
      row.setAttribute('aria-selected', 'false')
    })

    this.activeRow = null
  }

  onClick = (event) => {
    if (event.target.closest('[data-ucd-context-menu]')) return

    const menuButton = event.target.closest('.ucd-row-menu-button')

    if (menuButton) {
      const row = this.findRow(menuButton)
      const menu = this.getMenu(row)

      if (!row || !menu) return

      event.preventDefault()
      event.stopPropagation()

      this.selectRow(row, false)

      const rect = menuButton.getBoundingClientRect()

      this.openContextMenu(
        menu,
        rect.right,
        rect.bottom + 4
      )

      return
    }
    if (event.target.closest('a, button, input, label, select, textarea')) return

    const row = this.findRow(event.target)

    if (!row) {
      this.clearSelection()
      return
    }

    this.selectRow(row, event.metaKey || event.ctrlKey)
    this.closeContextMenu()
  }

  onDoubleClick = (event) => {
    if (event.target.closest('a, button, input, label, select, textarea')) return

    const row = this.findRow(event.target)
    const openUrl = row?.dataset.ucdOpenUrl

    if (!openUrl) return

    window.Turbo?.visit
      ? window.Turbo.visit(openUrl)
      : window.location.assign(openUrl)
  }

  onContextMenu = (event) => {
    const row = this.findRow(event.target)

    if (!row) return

    const menu = this.getMenu(row)

    if (!menu) return

    event.preventDefault()
    event.stopPropagation()

    this.selectRow(row, false)
    this.openContextMenu(menu, event.clientX, event.clientY)
  }

  openContextMenu = (menu, clientX, clientY) => {
    this.closeContextMenu()

    /*
     * The Finder container uses overflow:hidden.
     * Moving the menu to document.body prevents clipping and gives
     * the contextual menu a native desktop-style behavior.
     */
    if (menu.parentElement !== document.body) {
      document.body.appendChild(menu)
    }

    menu.hidden = false
    menu.setAttribute('aria-hidden', 'false')
    menu.classList.remove('ucd-context-menu-visible')

    menu.style.position = 'fixed'
    menu.style.left = '0px'
    menu.style.top = '0px'
    menu.style.zIndex = '10000'

    const rect = menu.getBoundingClientRect()
    const margin = 8

    const left = Math.min(
      clientX,
      window.innerWidth - rect.width - margin
    )

    const top = Math.min(
      clientY,
      window.innerHeight - rect.height - margin
    )

    menu.style.left = `${Math.max(margin, left)}px`
    menu.style.top = `${Math.max(margin, top)}px`

    requestAnimationFrame(() => {
      menu.classList.add('ucd-context-menu-visible')
    })

    this.activeMenu = menu
  }

  closeContextMenu = () => {
    if (!this.activeMenu) return

    this.activeMenu.classList.remove('ucd-context-menu-visible')
    this.activeMenu.hidden = true
    this.activeMenu.setAttribute('aria-hidden', 'true')
    this.activeMenu.style.removeProperty('left')
    this.activeMenu.style.removeProperty('top')
    this.activeMenu.style.removeProperty('position')
    this.activeMenu.style.removeProperty('z-index')

    this.activeMenu = null
  }

  onDocumentClick = (event) => {
    if (this.activeMenu?.contains(event.target)) return

    this.closeContextMenu()
  }

  onKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.closeContextMenu()
      return
    }

    if (
      event.key === 'Enter' &&
      this.activeRow &&
      !event.target.matches('input, textarea, select')
    ) {
      const openUrl = this.activeRow.dataset.ucdOpenUrl

      if (!openUrl) return

      event.preventDefault()

      window.Turbo?.visit
        ? window.Turbo.visit(openUrl)
        : window.location.assign(openUrl)
    }
  }
}
